export type EmployeeWorkAreaFilter = 'all' | 'office' | 'field';

export const WORK_AREA_FILTER_OPTIONS: Array<{ value: EmployeeWorkAreaFilter; label: string }> = [
  { value: 'all', label: 'Alle' },
  { value: 'office', label: 'Büro' },
  { value: 'field', label: 'Außendienst' },
];

export function workAreaFilterOptionsForHrAccess(
  canAccessInternal: boolean,
): Array<{ value: EmployeeWorkAreaFilter; label: string }> {
  if (canAccessInternal) return WORK_AREA_FILTER_OPTIONS;
  return WORK_AREA_FILTER_OPTIONS.filter((option) => option.value !== 'office');
}

export function sanitizeWorkAreaFilter(
  filter: EmployeeWorkAreaFilter,
  canAccessInternal: boolean,
): EmployeeWorkAreaFilter {
  if (!canAccessInternal && filter === 'office') return 'all';
  return filter;
}

const STORAGE_KEY = 'meisterpfad_employees_work_area_filter';

type StoredWorkAreaFilters = {
  lastUserId?: string;
  lastFilter?: EmployeeWorkAreaFilter;
  byUser: Record<string, EmployeeWorkAreaFilter>;
};

function isWorkAreaFilter(value: unknown): value is EmployeeWorkAreaFilter {
  return value === 'all' || value === 'office' || value === 'field';
}

function parseStoredFilters(raw: string | null): StoredWorkAreaFilters {
  if (!raw) return { byUser: {} };
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return { byUser: {} };

    // Legacy shape: { [userId]: filter }
    if (!('byUser' in parsed)) {
      const byUser: Record<string, EmployeeWorkAreaFilter> = {};
      for (const [userId, value] of Object.entries(parsed)) {
        if (isWorkAreaFilter(value)) byUser[userId] = value;
      }
      const entries = Object.entries(byUser);
      return {
        byUser,
        lastUserId: entries.length === 1 ? entries[0][0] : undefined,
        lastFilter: entries.length === 1 ? entries[0][1] : undefined,
      };
    }

    const row = parsed as {
      lastUserId?: unknown;
      lastFilter?: unknown;
      byUser?: unknown;
    };
    const byUser: Record<string, EmployeeWorkAreaFilter> = {};
    if (row.byUser && typeof row.byUser === 'object') {
      for (const [userId, value] of Object.entries(row.byUser)) {
        if (isWorkAreaFilter(value)) byUser[userId] = value;
      }
    }
    return {
      byUser,
      lastUserId: typeof row.lastUserId === 'string' ? row.lastUserId : undefined,
      lastFilter: isWorkAreaFilter(row.lastFilter) ? row.lastFilter : undefined,
    };
  } catch {
    return { byUser: {} };
  }
}

/** Sync read for first paint — uses last written filter so reload does not flash "Alle". */
export function readInitialEmployeeWorkAreaFilter(): EmployeeWorkAreaFilter {
  if (typeof localStorage === 'undefined') return 'all';
  const stored = parseStoredFilters(localStorage.getItem(STORAGE_KEY));
  if (stored.lastFilter) return stored.lastFilter;
  if (stored.lastUserId && stored.byUser[stored.lastUserId]) {
    return stored.byUser[stored.lastUserId];
  }
  return 'all';
}

export function readEmployeeWorkAreaFilter(userId: string | null | undefined): EmployeeWorkAreaFilter {
  if (!userId || typeof localStorage === 'undefined') return 'all';
  const stored = parseStoredFilters(localStorage.getItem(STORAGE_KEY));
  return stored.byUser[userId] ?? 'all';
}

export function writeEmployeeWorkAreaFilter(
  userId: string | null | undefined,
  filter: EmployeeWorkAreaFilter,
): void {
  if (typeof localStorage === 'undefined') return;
  const stored = parseStoredFilters(localStorage.getItem(STORAGE_KEY));
  stored.lastFilter = filter;
  if (userId) {
    stored.byUser[userId] = filter;
    stored.lastUserId = userId;
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
}

export function matchesEmployeeWorkAreaFilter(
  workArea: string | null | undefined,
  filter: EmployeeWorkAreaFilter,
): boolean {
  if (filter === 'all') return true;
  return String(workArea ?? '').trim() === filter;
}
